import { h, Component, ComponentChild } from 'preact'
import QuickLinkModal from './QuickLinkModal'
import Modal from '../../components/reactModal'
import {
  arrayMove,
  SortableContainer,
  SortableElement
} from 'react-sortable-hoc'
import QuickLink from './QuickLink'
import QuickLinksList from './QuickLinksList'
import Hover from '../hover/Hover'
import EditOrDeleteQuickLink from './EditOrDeleteQuickLink'
import QuickLinksSettings, {QuickLinksSettingsType} from './QuickLinksSettings'
import Panel from '../../control-components/panel'
import InputGroup from '../../control-components/ui-components/InputGroup'
import Button from '../../control-components/ui-components/Button'

interface PropsWithChildren {
  children: ComponentChild
}

const SortableList = SortableContainer(({ children }: PropsWithChildren) => children)
const SortableItem = SortableElement(({ children }: PropsWithChildren) => children)

export interface QuicklinkType {
  description: string
  iconSrc: null | string
  id: string
  imageSrc: null | string
  openNewTab: boolean
  title: string
  url: string
}

interface Props {
  settings: QuickLinksSettingsType
  isActive: boolean
  title: string
  quickLinks: Array<QuicklinkType>
  onChange: (changes: {
    quickLinks: Array<QuicklinkType>
    settings: QuickLinksSettingsType
    title: string
  }) => void
}

interface State {
  title: string
  quickLinks: Array<QuicklinkType>
  settings: QuickLinksSettingsType
  showModal: boolean
  selectedItem: null | QuicklinkType
  selectedIndex: null | number
}

export class QuickLinksManager extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      title: props.title,
      quickLinks: props.quickLinks,
      settings: props.settings,
      showModal: false,
      selectedItem: null,
      selectedIndex: null
    }
  }

  shouldComponentUpdate (nextProps: Props, nextState: State) {
    return this.props.quickLinks !== nextProps.quickLinks ||
      this.props.settings !== nextProps.settings ||
      this.props.isActive !== nextProps.isActive ||
      this.state !== nextState
  }

  toggleModal (show = true, item: null|QuicklinkType = null, idx: null|number = null) {
    this.setState({ showModal: show, selectedItem: item, selectedIndex: idx })
  }

  deleteQuickLink (quickLink: QuicklinkType) {
    this.setState({
      quickLinks: this.state.quickLinks.filter(el => el !== quickLink)
    })
    this.props.onChange({
      quickLinks: this.state.quickLinks,
      settings: this.state.settings,
      title: this.state.title
    })
  }

  addQuickLink (quickLink: QuicklinkType) {
    const selectedIndex = this.state.selectedIndex
    const quickLinks = this.state.quickLinks.concat([])

    if (selectedIndex !== null) {
      quickLinks.splice(selectedIndex, 1, quickLink)
    } else {
      quickLinks.push(quickLink)
    }

    this.setState({
      quickLinks: quickLinks,
      showModal: false
    })

    this.props.onChange({
      quickLinks: this.state.quickLinks,
      settings: this.state.settings,
      title: this.state.title
    })
  }

  onSortEnd (oldIndex: number, newIndex: number) {
    const quickLinks = this.state.quickLinks.concat([])
    this.setState({ quickLinks: arrayMove(quickLinks, oldIndex, newIndex) })
    this.props.onChange({
      quickLinks: this.state.quickLinks,
      settings: this.state.settings,
      title: this.state.title
    })
  }

  onSettingsChanged (settings: Partial<QuickLinksSettingsType>) {
    this.setState({
      settings: Object.assign({}, this.state.settings, settings)
    })
    this.props.onChange({
      quickLinks: this.state.quickLinks,
      settings: this.state.settings,
      title: this.state.title
    })
  }

  onChangeTitle (newTitle: string) {
    this.setState({ title: newTitle })
    this.props.onChange({
      quickLinks: this.state.quickLinks,
      settings: this.state.settings,
      title: newTitle
    })
  }

  render (props: Props, state: State) {
    const quickLinks = state.quickLinks
    const isForum = window.inSidedData?.communityId

    return (
        <div className='quick-link-manager js-quick-links' id='quickLinksManager'>
          <InputGroup
            title='Title'
            type='text'
            onChange={(e: ChangeEvent) => { this.onChangeTitle(e.target.value) }}
            value={state.title} />
          <QuickLinksSettings
            settings={state.settings}
            onChange={(settings) => this.onSettingsChanged(settings)}
          />
          <SortableList
            onSortEnd={({ oldIndex, newIndex } : {oldIndex: number, newIndex: number}) => this.onSortEnd(oldIndex, newIndex)}
            axis='xy'>
            <QuickLinksList>
              {quickLinks.map((item, idx) =>
                <SortableItem index={idx} key={item.id}>
                  <Hover hoverComponent={<EditOrDeleteQuickLink
                    onDelete={() => this.deleteQuickLink(item)}
                    onEdit={() => this.toggleModal(true, item, idx)} />}>
                    <QuickLink
                      item={item}
                      enableBorder={state.settings.enableBorder}
                      borderRadius={state.settings.borderRadius}
                      isVisibleDesktop
                    />
                  </Hover>
                </SortableItem>
              )}
            </QuickLinksList>
          </SortableList>
          <Button
            type='button'
            class={`${!isForum ? 'btn-primary' : ''} btn--insided-secondary-brand`}
            onClick={() => this.toggleModal()}
          >
            Add quick link
          </Button>
          <Modal isOpen={state.showModal} onClose={() => this.toggleModal(false)}>
            <QuickLinkModal
              onSubmit={(quickLink: QuicklinkType) => this.addQuickLink(quickLink)}
              item={state.selectedItem} />
          </Modal>
        </div>
    )
  }
}


interface PanelProps extends Props {
  dragHandle: JSX.Element | null
  switcher: JSX.Element | null
}

const QuickLinksManagerPanel = (props: PanelProps) => {
  return <Panel
        collapsible
        customClass='homepage-widget'
        title={<div className='panel-title-with-switch'>
          <div className='homepage-reorderable-panel-title'>
            { props.dragHandle }
            <span>Quick links</span>
          </div>
          { props.switcher }
        </div>}>
    <QuickLinksManager {...props} />
  </Panel>
}

export default QuickLinksManagerPanel
